'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@/components/ui/Dialog';
import type { MentorshipProgramDetail } from '@/shared/job-mentorship';
import {
  CheckboxQuestion,
  RadioQuestion,
  RequiredLabel,
  SingleChoiceFromOptions,
} from '../shared/MentorFormControls';
import { PRIMARY_BTN, PRIMARY_BG } from '../shared/mentorship.constants';
import { postMentorshipSurvey } from '@/features/mentorship';
import { toast } from 'react-toastify';

type Q = MentorshipProgramDetail['questions'][number];

function sortedActiveQuestions(questions: Q[]): Q[] {
  return [...questions]
    .filter((q) => q.active)
    .sort((a, b) => a.ordering - b.ordering);
}

function optionItemsForQuestion(q: Q) {
  return [...q.questionData]
    .filter((d) => d.active)
    .sort((a, b) => a.ordering - b.ordering)
    .map((d) => ({
      id: String(d.dataId),
      title: d.caption.trim() || `Сонголт ${d.dataId}`,
    }));
}

type Draft = Record<number, string>;

export function buildAnswersPayload(
  questions: Q[],
  draft: Draft
): { questionId: number; answer: string }[] {
  return questions.map((q) => ({
    questionId: q.id,
    answer: (draft[q.id] ?? '').trim(),
  }));
}

function QuestionField({
  q,
  draft,
  setAnswer,
}: {
  q: Q;
  draft: Draft;
  setAnswer: (questionId: number, value: string) => void;
}) {
  switch (q.qtype) {
    case 'yesno':
      return (
        <RadioQuestion
          label={<RequiredLabel>{q.question}</RequiredLabel>}
          name={`mq_${q.id}`}
          value={draft[q.id] ?? ''}
          onChange={(v) => setAnswer(q.id, v)}
        />
      );
    case 'option':
      return (
        <SingleChoiceFromOptions
          label={<RequiredLabel>{q.question}</RequiredLabel>}
          name={`mq_${q.id}`}
          options={optionItemsForQuestion(q)}
          value={draft[q.id] ?? ''}
          onChange={(v) => setAnswer(q.id, v)}
        />
      );
    case 'multicheck':
      return (
        <CheckboxQuestion
          label={<RequiredLabel>{q.question}</RequiredLabel>}
          options={optionItemsForQuestion(q)}
          value={
            draft[q.id] ? draft[q.id].split(',').filter(Boolean) : []
          }
          onChange={(ids) => setAnswer(q.id, ids.join(','))}
          columns={2}
        />
      );
    case 'text':
      return (
        <div className="flex flex-col gap-2">
          <p className="font-medium text-black">
            <RequiredLabel>{q.question}</RequiredLabel>
          </p>
          <textarea
            value={draft[q.id] ?? ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            rows={4}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#0AC1CE]"
          />
        </div>
      );
    case 'rate':
      return (
        <div className="flex flex-col gap-2">
          <p className="font-medium text-black">
            <RequiredLabel>{q.question}</RequiredLabel>
          </p>
          <input
            type="number"
            min={1}
            max={5}
            step={1}
            value={draft[q.id] ?? ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-24 focus:outline-none focus:border-[#0AC1CE]"
          />
        </div>
      );
    default:
      return (
        <p className="text-amber-700 text-xs">
          Дэмжигдээгүй асуултын төрөл: {q.qtype}
        </p>
      );
  }
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  programId: number;
  applyId: number | null;
  questions: Q[];
  onSuccess: () => void;
};

export default function ApiMentorshipSurveyDialog({
  isOpen,
  onClose,
  programId,
  applyId,
  questions,
  onSuccess,
}: Props) {
  const list = useMemo(() => sortedActiveQuestions(questions), [questions]);
  const [draft, setDraft] = useState<Draft>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraft({});
    }
  }, [isOpen, programId]);

  const setAnswer = useCallback((questionId: number, value: string) => {
    setDraft((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const validate = (): boolean => {
    const supported = list.filter((q) =>
      ['yesno', 'option', 'multicheck', 'text', 'rate'].includes(q.qtype)
    );
    for (const q of supported) {
      const v = (draft[q.id] ?? '').trim();
      if (!v) {
        toast.error('Бүх асуултад хариулна уу.');
        return false;
      }
      if (q.qtype === 'rate') {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 1 || n > 5) {
          toast.error('Үнэлгээг 1–5 хооронд өгнө үү.');
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!applyId) {
      toast.error('Бүртгэлийн мэдээлэл олдсонгүй. Дахин оролдоно уу.');
      return;
    }
    if (!validate()) return;

    const supportedTypes = new Set([
      'yesno',
      'option',
      'multicheck',
      'text',
      'rate',
    ]);
    const supportedQs = list.filter((q) => supportedTypes.has(q.qtype));
    const answers = buildAnswersPayload(supportedQs, draft).filter(
      (a) => a.answer.length > 0
    );
    if (answers.length === 0) {
      toast.error('Хамгийн багадаа нэг хариулт оруулна уу.');
      return;
    }

    setSubmitting(true);
    try {
      await postMentorshipSurvey({ programId, applyId, answers });
      toast.success('Амжилттай илгээгдлээ');
      onClose();
      onSuccess();
    } catch {
      toast.error('Илгээхэд алдаа гарлаа. Дахин оролдоно уу.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Асуумжууд"
      maxWidth="xl"
      isSticky
    >
      <div className="flex flex-col gap-6 text-sm text-[#414651] pb-4">
        {list.map((q) => (
          <QuestionField key={q.id} q={q} draft={draft} setAnswer={setAnswer} />
        ))}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            className={PRIMARY_BTN}
            style={{ background: PRIMARY_BG }}
          >
            {submitting ? 'Илгээж байна...' : 'Хариулт илгээх'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
